import { prefetchBriefLlm, useBriefLlm } from "./useBriefLlm.js";
import { cleanInsightHeadings, insightHeadingsPrompt } from "../lib/forecast/crossInsights.js";

/**
 * Each post-forecast insight's category label and headline for the brief's cards, written once
 * per report by the small LLM and cached in this browser. A map of insight ref → {label, title},
 * or null until it arrives, when writing fails, or while `enabled` is false.
 *
 * @param {{ enabled: boolean, question: string, insights: Array<{ref: string, text: string}>|null }} deps
 * @returns {{ headings: Map<string, {label: string, title: string}>|null, pending: boolean }}
 */
export function useInsightHeadings({ enabled, question, insights }) {
  const spec = insightHeadingsSpec({ question, insights });
  const { value, pending } = useBriefLlm({ ...spec, enabled: !!(enabled && spec.ready) });
  return {
    headings: value ? new Map(value.insights.map((h) => [h.ref, h])) : null,
    pending,
  };
}

/** The headings request; `ready` when there are insights to head. */
function insightHeadingsSpec({ question, insights }) {
  const list = (insights || []).map((it) => ({ ref: it.ref, text: it.text }));
  return {
    ready: list.length > 0,
    name: "briefInsightHeadings",
    version: 1,
    inputs: [question, list],
    prompt: () => insightHeadingsPrompt({ question, insights: list }),
    clean: (text) => {
      const out = cleanInsightHeadings(text);
      // Every insight needs its heading, by ref: a partial answer is no answer.
      return out && list.every((it) => out.insights.some((h) => h.ref === it.ref)) ? out : null;
    },
  };
}

/** Starts writing the insight headings before the brief opens (see prefetchBriefLlm). */
export function prefetchInsightHeadings(deps) {
  const spec = insightHeadingsSpec(deps);
  if (spec.ready) prefetchBriefLlm(spec);
}
